import { Box, Divider, List, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import type { DrillDownLayer, DrillDownPage } from "../../../../store/types/drillDownPageState.types";
import { createDrillDownPageAction } from "../../../../store/utils/storeUtils";
import DrillDownComponentItem from "./DrillDownComponentItem";
import DrillDownFormulaPreview from "./DrillDownFormulaPreview";

interface Props {
  page: DrillDownPage;
}

export default function DrillDownComponentList({ page }: Props) {
  const dispatch = useDispatch();
  const activeIndex = page.layer.selectedSubLayerIndex ?? 0;

  const handleSelect = useCallback(
    (index: number) => {
      const selectedSubLayer = (page.layer.subLayers ?? [])[index];
      if (!selectedSubLayer || selectedSubLayer.disabled || index === activeIndex) {
        return;
      }

      if (index !== activeIndex) {
        dispatch(createDrillDownPageAction("setSelectedSubLayer", { pageId: page.pageId, index }));
      }
    },
    [activeIndex, dispatch, page.layer.subLayers, page.pageId]
  );

  const columnName = page.layer.measure?.config.customLabel || page.layer.measure?.meta.caption;

  return (
    <Stack sx={{ borderRight: 1, borderColor: "divider", backgroundColor: "background.paper", height: "100%" }}>
      {columnName && (
        <Stack sx={{ p: 2, gap: 0.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {columnName}
          </Typography>
          {page.layer.cell.formattedValue && (
            <Typography variant="caption" color="textSecondary">
              Value: {page.layer.cell.formattedValue}
            </Typography>
          )}
        </Stack>
      )}
      <Divider />
      <Stack sx={{ p: 2, gap: 1, height: "100%" }}>
        {page.layer.formulaPreview && (
          <>
            <Typography variant="subtitle1">Formula</Typography>
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                border: 1,
                borderColor: "divider",
                borderRadius: 0.75,
                backgroundColor: "background.grey",
              }}
            >
              <DrillDownFormulaPreview formula={page.layer.formulaPreview} />
            </Box>
            {page.layer.formulaValuesPreview && (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 0.75,
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  = {page.layer.formulaValuesPreview}
                </Typography>
              </Box>
            )}
          </>
        )}
        <ScrollableFlexContainer>
          <List dense disablePadding sx={{ flex: 1, overflowY: "auto", mt: 0.5 }}>
            {(page.layer.subLayers ?? []).map((subLayer, index) => (
              <Box key={getLayerKey(subLayer, index)}>
                <DrillDownComponentItem
                  component={subLayer}
                  field={subLayer.measure}
                  index={index}
                  active={index === activeIndex}
                  onSelect={handleSelect}
                />
              </Box>
            ))}
          </List>
        </ScrollableFlexContainer>
      </Stack>
    </Stack>
  );
}

function getLayerKey(layer: DrillDownLayer, index: number): string {
  return `${layer.drillDownId ?? "preloaded"}-${layer.measure?.config.guid ?? ""}-${index}`;
}
